import { useEffect, useMemo, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { g, p, pa, pu, del, errMsg } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { usePlan } from "@/context/PlanContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { ListaAttesa } from "@/components/ListaAttesa";
import { Caparra, CaparraConfig } from "@/components/Caparra";
import { Users, Trash2, Wand2, Clock } from "lucide-react";

const iso = (d) => d.toISOString().slice(0, 10);
const oggi = () => iso(new Date());
const STATI = ["attesa", "arrivata", "no_show", "annullata"];
const ETICHETTA = { attesa: "In attesa", arrivata: "Arrivata", no_show: "Assente", annullata: "Annullata" };
const COLORE = {
  attesa: "bg-amber-100 text-amber-900",
  arrivata: "bg-emerald-100 text-emerald-900",
  no_show: "bg-rose-100 text-rose-900",
  annullata: "bg-slate-200 text-slate-500",
};

const giornoIt = (s) =>
  new Date(s + "T12:00:00").toLocaleDateString("it-IT", { weekday: "short", day: "2-digit", month: "2-digit" });

const settimanaDi = (base) => {
  const d = new Date(base + "T12:00:00");
  const l = new Date(d);
  l.setDate(d.getDate() - ((d.getDay() + 6) % 7));
  return [...Array(7)].map((_, i) => {
    const x = new Date(l);
    x.setDate(l.getDate() + i);
    return iso(x);
  });
};

const meseDi = (base) => {
  const d = new Date(base + "T12:00:00");
  const primo = new Date(d.getFullYear(), d.getMonth(), 1);
  const ultimo = new Date(d.getFullYear(), d.getMonth() + 1, 0);
  const celle = [];
  for (let i = 0; i < (primo.getDay() + 6) % 7; i++) celle.push(null);
  for (let g = 1; g <= ultimo.getDate(); g++) celle.push(iso(new Date(d.getFullYear(), d.getMonth(), g)));
  return celle;
};

export default function Prenotazioni() {
  const { zones, user, settings, refresh } = useApp();
  const { handleError, refreshPlan } = usePlan();
  const nav = useNavigate();
  const [vista, setVista] = useState(localStorage.getItem("rh_pren_vista") || "giorno");
  const [data, setData] = useState(oggi());
  const [list, setList] = useState([]);
  const [tavoli, setTavoli] = useState([]);
  const [nuova, setNuova] = useState({ customer_name: "", phone: "", email: "", time: "", people: 2, zone: "", note: "" });
  const [promemoria, setPromemoria] = useState({ date: "", rows: [] });
  const [scelti, setScelti] = useState([]);
  const [rubrica, setRubrica] = useState([]);
  const [consigli, setConsigli] = useState(null);
  const [modifica, setModifica] = useState(null);
  const [turni, setTurni] = useState((settings?.turns || ["19:30", "21:15"]).join(", "));
  const titolare = user?.role === "titolare";
  const direzione = titolare || user?.role === "direttore";
  const orariTurno = settings?.turns || ["19:30", "21:15"];
  const [squadra, setSquadra] = useState([]);

  useEffect(() => {
    if (direzione) g("/staff").then(setSquadra).catch(() => setSquadra([]));
  }, [direzione]);

  const periodo = useMemo(() => {
    if (vista === "giorno") return { date: data };
    if (vista === "settimana") {
      const s = settimanaDi(data);
      return { date_from: s[0], date_to: s[6] };
    }
    const c = meseDi(data).filter(Boolean);
    return { date_from: c[0], date_to: c[c.length - 1] };
  }, [vista, data]);

  const load = useCallback(async () => {
    const [r, t] = await Promise.all([g("/reservations", periodo), g("/tables")]);
    setList(r);
    setTavoli(t);
    g("/reservations/reminders").then(setPromemoria).catch(() => setPromemoria({ date: "", rows: [] }));
  }, [periodo]);

  useEffect(() => {
    load();
    const i = setInterval(load, 20000);
    return () => clearInterval(i);
  }, [load]);

  useEffect(() => {
    localStorage.setItem("rh_pren_vista", vista);
  }, [vista]);

  useEffect(() => {
    if (nuova.customer_name.length < 2) return setRubrica([]);
    const id = setTimeout(() => g("/customers", { q: nuova.customer_name }).then(setRubrica).catch(() => setRubrica([])), 300);
    return () => clearTimeout(id);
  }, [nuova.customer_name]);

  const occupatiInFascia = useMemo(() => {
    const m = (h) => (h ? Number(h.slice(0, 2)) * 60 + Number(h.slice(3, 5)) : 0);
    const ids = new Set();
    list.forEach((r) => {
      if (r.date !== data || r.status !== "attesa") return;
      if (nuova.time && Math.abs(m(r.time) - m(nuova.time)) > 90) return;
      (r.table_ids || []).forEach((x) => ids.add(x));
    });
    return ids;
  }, [list, data, nuova.time]);

  const salva = async () => {
    if (!nuova.customer_name) return toast.error("Scrivi almeno il nome");
    if (!nuova.time) return toast.error("Scegli l'ora o il turno");
    try {
      await p("/reservations", {
        ...nuova, date: data, people: Number(nuova.people) || 1, table_ids: scelti,
        turn: orariTurno.indexOf(nuova.time) >= 0 ? `${orariTurno.indexOf(nuova.time) + 1}º` : "",
      });
    } catch (e) {
      if (handleError(e)) return;
      return toast.error(errMsg(e));
    }
    toast.success("Prenotazione registrata");
    setNuova({ customer_name: "", phone: "", email: "", time: "", people: 2, zone: "", note: "" });
    setScelti([]);
    setConsigli(null);
    load();
    refreshPlan();
  };

  const consiglia = async () => {
    const r = await g("/floor/suggest", { guests: Number(nuova.people) || 2, zone: nuova.zone, date: data, time: nuova.time });
    setConsigli(r);
    const primo = (r.single[0] || r.pairs[0]);
    if (primo) setScelti(primo.table_ids);
    else toast.error("Nessun tavolo libero adatto in quella fascia");
  };

  const cambia = async (r, body) => {
    const esito = await pa(`/reservations/${r.id}`, body);
    const primo = esito?.waitlist_match?.[0];
    if (primo) {
      toast.success(`Posto libero: in lista c'è ${primo.name} per ${primo.people} persone`, {
        description: "Aprilo dalla lista d'attesa per avvisarlo",
      });
    }
    load();
  };

  const accomoda = async (r) => {
    try {
      const res = await p(`/reservations/${r.id}/seat`, { by: user.name });
      toast.success(`Tavolo ${res.table_name} aperto per ${r.customer_name}`);
      nav(`/comanda/${res.order_id}`);
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const perGiorno = (d) => list.filter((r) => r.date === d && r.status !== "annullata");
  const coperti = (d) => perGiorno(d).reduce((s, r) => s + (r.people || 0), 0);
  const capienza = tavoli.reduce((s, t) => s + (t.seats || 0), 0);
  const delGiorno = list.filter((r) => r.date === data);
  const daTenere = delGiorno.filter((r) => r.status === "attesa" && (r.table_ids || []).length).length;

  const scheda = (r) => (
    <Card key={r.id} className="p-4" data-testid={`reservation-${r.id}`}>
      <div className="flex items-start gap-3">
        <span className="font-num rounded-md bg-slate-900 px-2.5 py-1.5 text-sm font-bold text-white">{r.time}</span>
        <div className="min-w-0 flex-1">
          <p className="truncate font-semibold">
            {r.customer_name}
            {r.customer_visits > 2 && (
              <span className="ml-2 rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-bold text-amber-900" data-testid={`res-abituale-${r.id}`}>
                abituale · {r.customer_visits} volte
              </span>
            )}
            {r.customer_no_shows > 0 && (
              <span className="ml-2 rounded-full bg-rose-100 px-2 py-0.5 text-[10px] font-bold text-rose-900" data-testid={`res-noshow-${r.id}`}>
                {r.customer_no_shows} assenze
              </span>
            )}
          </p>
          <p className="text-xs text-slate-500">
            <Users className="mr-1 inline h-3 w-3" />
            {r.people} pax{r.turn && ` · ${r.turn} turno`}{r.zone && ` · ${r.zone}`}
            {r.table_name && ` · tavolo ${r.table_name}`}
            {r.phone && ` · ${r.phone}`}
          </p>
          {r.customer_favourite_table && !r.table_name && (
            <p className="text-[11px] text-slate-400">preferisce il {r.customer_favourite_table}</p>
          )}
          <p className="text-[11px] text-slate-400">
            resta a tavola fino alle {r.ends_at} circa ({r.duration_min} min)
          </p>
          {(r.customer_allergens?.length > 0 || r.customer_requests?.length > 0 || r.occasion) && (
            <div className="mt-1 flex flex-wrap gap-1" data-testid={`res-scheda-${r.id}`}>
              {r.occasion && (
                <span className="rounded-full bg-fuchsia-100 px-2 py-0.5 text-[10px] font-bold text-fuchsia-900">
                  {r.occasion} oggi
                </span>
              )}
              {(r.customer_allergens || []).map((a) => (
                <span key={a} className="rounded-full bg-rose-100 px-2 py-0.5 text-[10px] font-bold text-rose-900">
                  {a}
                </span>
              ))}
              {(r.customer_requests || []).map((a) => (
                <span key={a} className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-semibold text-slate-600">
                  {a}
                </span>
              ))}
            </div>
          )}
          {r.note && <p className="mt-1 rounded bg-amber-50 px-2 py-1 text-xs font-medium text-amber-900">{r.note}</p>}
        </div>
        <button data-testid={`delete-reservation-${r.id}`} className="text-slate-400 hover:text-rose-600"
                onClick={async () => {
                  const esito = await del(`/reservations/${r.id}`);
                  const primo = esito?.waitlist_match?.[0];
                  toast.success("Prenotazione cancellata", {
                    description: primo
                      ? `In lista d'attesa c'è ${primo.name} per ${primo.people} persone: avvisalo dalla lista d'attesa`
                      : undefined,
                  });
                  load();
                }}>
          <Trash2 className="h-4 w-4" />
        </button>
      </div>

      <div className="mt-3 flex flex-wrap gap-1.5">
        {r.status === "attesa" && (
          <Button size="sm" className="h-9" data-testid={`res-seat-${r.id}`} onClick={() => accomoda(r)}>
            Accomoda
          </Button>
        )}
        {STATI.map((s) => (
          <button key={s} data-testid={`res-status-${r.id}-${s}`} onClick={() => cambia(r, { status: s })}
                  className={`rounded-full px-2.5 py-1 text-[11px] font-semibold ${r.status === s ? COLORE[s] : "bg-slate-100 text-slate-600"}`}>
            {ETICHETTA[s]}
          </button>
        ))}
        <button data-testid={`res-edit-${r.id}`} onClick={() => setModifica(modifica === r.id ? null : r.id)}
                className="rounded-full bg-slate-100 px-2.5 py-1 text-[11px] font-semibold text-slate-600">
          Modifica
        </button>
      </div>

      <Caparra r={r} onCambio={load} />

      {modifica === r.id && (
        <div className="mt-3 space-y-2" data-testid={`res-edit-panel-${r.id}`}>
          <div className="grid grid-cols-3 gap-2">
            <Input type="date" defaultValue={r.date} onBlur={(e) => cambia(r, { date: e.target.value })} data-testid={`res-edit-date-${r.id}`} />
            <Input type="time" defaultValue={r.time} onBlur={(e) => cambia(r, { time: e.target.value })} data-testid={`res-edit-time-${r.id}`} />
            <Input type="number" min="1" defaultValue={r.people} onBlur={(e) => cambia(r, { people: Number(e.target.value) })} data-testid={`res-edit-people-${r.id}`} />
          </div>
          <div className="flex flex-wrap gap-1">
            {tavoli.map((x) => {
              const dentro = (r.table_ids || []).includes(x.id);
              return (
                <button key={x.id} data-testid={`res-edit-table-${r.id}-${x.number}`}
                        onClick={() => cambia(r, { table_ids: dentro ? r.table_ids.filter((i) => i !== x.id) : [...(r.table_ids || []), x.id] })}
                        className={`rounded px-2 py-1 text-[11px] font-semibold ${dentro ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-600"}`}>
                  {x.number}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </Card>
  );

  return (
    <div className="fade-up space-y-5" data-testid="prenotazioni-page">
      <div>
        <p className="text-[10px] font-bold uppercase tracking-[0.24em] text-slate-500">Sala</p>
        <h1 className="mt-1 text-3xl font-bold tracking-tight sm:text-4xl">Prenotazioni</h1>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <div className="flex rounded-full border border-slate-200 p-0.5">
          {["giorno", "settimana", "mese", "attesa"].map((v) => (
            <button key={v} data-testid={`vista-${v}`} onClick={() => setVista(v)}
                    className={`rounded-full px-3 py-1.5 text-xs font-semibold capitalize ${vista === v ? "bg-slate-900 text-white" : "text-slate-500"}`}>
              {v === "attesa" ? "lista d'attesa" : v}
            </button>
          ))}
        </div>
        <Input type="date" value={data} onChange={(e) => setData(e.target.value)} className="h-10 w-auto" data-testid="vista-data" />
        {vista === "giorno" && (
          <p className="text-xs font-semibold text-slate-500" data-testid="giorno-riepilogo">
            {coperti(data)} coperti attesi · {daTenere} tavoli da tenere liberi
          </p>
        )}
      </div>

      {vista === "giorno" && (
        <>
          <Card className="space-y-3 p-4" data-testid="quick-reservation-bar">
            <p className="text-xs font-bold uppercase tracking-[0.16em] text-slate-500">Nuova prenotazione</p>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
              <div className="relative">
                <label className="text-[11px] font-bold uppercase tracking-wider text-slate-500" htmlFor="res-nome">Nome del cliente</label>
                <Input id="res-nome" className="mt-1" placeholder="Es. Rossi" value={nuova.customer_name} onChange={(e) => setNuova({ ...nuova, customer_name: e.target.value })} data-testid="res-name-input" />
                {rubrica.length > 0 && (
                  <div className="absolute z-20 mt-1 w-full rounded-md border border-slate-200 bg-white shadow-lg" data-testid="rubrica-suggerimenti">
                    {rubrica.slice(0, 4).map((c) => (
                      <button key={c.id} data-testid={`rubrica-${c.id}`} className="block w-full px-3 py-2 text-left text-xs hover:bg-slate-50"
                              onClick={() => { setNuova({ ...nuova, customer_name: c.name, phone: c.phone || "", email: c.email || "", customer_id: c.id }); setRubrica([]); if (c.no_shows > 0) toast.warning(`Attenzione: ${c.name} ha disdetto all\u2019ultimo o non si è presentato ${c.no_shows} volte`); }}>
                        <b>{c.name}</b>
                        <span className="text-slate-500">
                          {c.visits ? ` · ${c.visits} visite` : ""}
                          {c.favourite_table ? ` · preferisce ${c.favourite_table}` : ""}
                          {c.no_shows ? ` · ${c.no_shows} assenze` : ""}
                        </span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
              <div>
                <label className="text-[11px] font-bold uppercase tracking-wider text-slate-500" htmlFor="res-tel">Telefono</label>
                <Input id="res-tel" className="mt-1" type="tel" placeholder="Per il promemoria del giorno prima" value={nuova.phone} onChange={(e) => setNuova({ ...nuova, phone: e.target.value })} data-testid="res-phone-input" />
              </div>
              <div>
                <label className="text-[11px] font-bold uppercase tracking-wider text-slate-500" htmlFor="res-pax">Quante persone</label>
                <Input id="res-pax" className="mt-1" type="number" min="1" value={nuova.people} onChange={(e) => setNuova({ ...nuova, people: e.target.value })} data-testid="res-people-input" />
              </div>
            </div>

            <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
              <div>
                <label className="text-[11px] font-bold uppercase tracking-wider text-slate-500" htmlFor="res-ora">A che ora</label>
                <Input id="res-ora" className="mt-1" type="time" value={nuova.time} onChange={(e) => setNuova({ ...nuova, time: e.target.value })} data-testid="res-time-input" />
              </div>
              <div>
                <label className="text-[11px] font-bold uppercase tracking-wider text-slate-500" htmlFor="res-zona">In quale zona</label>
                <select id="res-zona" className="mt-1 h-10 w-full rounded-md border border-slate-200 bg-white px-3 text-sm" value={nuova.zone}
                        onChange={(e) => setNuova({ ...nuova, zone: e.target.value })} data-testid="res-zone-select">
                  <option value="">Qualsiasi zona</option>
                  {zones.map((z) => <option key={z.id} value={z.name}>{z.name}</option>)}
                </select>
              </div>
              <div>
                <label className="text-[11px] font-bold uppercase tracking-wider text-slate-500" htmlFor="res-email">Email (facoltativa)</label>
                <Input id="res-email" className="mt-1" type="email" placeholder="Riceve il promemoria da sola" value={nuova.email}
                       onChange={(e) => setNuova({ ...nuova, email: e.target.value })} data-testid="res-email-input" />
              </div>
            </div>

            <div>
              <label className="text-[11px] font-bold uppercase tracking-wider text-slate-500" htmlFor="res-note">Note per la cucina e la sala</label>
              <Input id="res-note" className="mt-1" placeholder="Seggiolone, allergie, compleanno…" value={nuova.note}
                     onChange={(e) => setNuova({ ...nuova, note: e.target.value })} data-testid="res-note-input" />
            </div>

            <div>
              <p className="text-[11px] font-bold uppercase tracking-wider text-slate-500">Turni della sera</p>
              <div className="mt-1 flex flex-wrap items-center gap-2">
                {orariTurno.map((h, i) => (
                  <button key={h} data-testid={`turno-${i + 1}`} onClick={() => setNuova({ ...nuova, time: h })}
                          className={`rounded-full px-3 py-1.5 text-xs font-semibold ${nuova.time === h ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-600"}`}>
                    <Clock className="mr-1 inline h-3 w-3" />{i + 1}º turno · {h}
                  </button>
                ))}
                <Button variant="outline" className="h-10" onClick={consiglia} data-testid="suggest-btn">
                  <Wand2 className="mr-1.5 h-4 w-4" /> Trova il tavolo
                </Button>
              </div>
            </div>

            {consigli && (
              <p className="text-[11px] text-slate-500" data-testid="suggest-esito">
                {[...consigli.single, ...consigli.pairs].map((s) => s.name).join(" · ") || "nessuna proposta"}
              </p>
            )}

            <div>
              <p className="text-[11px] font-bold uppercase tracking-wider text-slate-500">
                Tavolo {Number(nuova.people) > 4 ? "— oltre 4 persone: tocca più tavoli per unirli" : "— tocca il tavolo da assegnare"}
              </p>
              <div className="mt-1.5 flex flex-wrap gap-1.5" data-testid="griglia-tavoli">
                {tavoli.filter((x) => !nuova.zone || x.zone === nuova.zone).map((x) => {
                  const preso = occupatiInFascia.has(x.id) || !!x.order;
                  const sel = scelti.includes(x.id);
                  return (
                    <button key={x.id} data-testid={`tavolo-${x.number}`} disabled={preso && !sel}
                            onClick={() => setScelti(sel ? scelti.filter((i) => i !== x.id) : [...scelti, x.id])}
                            className={`rounded-md px-2.5 py-1.5 text-[11px] font-semibold ${
                              sel ? "bg-slate-900 text-white"
                                : preso ? "bg-rose-50 text-rose-400 line-through"
                                  : "bg-slate-100 text-slate-700"}`}>
                      {x.number} · {x.seats}p
                    </button>
                  );
                })}
              </div>
              {scelti.length > 1 && (
                <p className="mt-1 text-[11px] font-semibold text-slate-600" data-testid="tavoli-uniti">
                  Tavoli uniti: {scelti.map((i) => tavoli.find((t) => t.id === i)?.number).join(" + ")}
                </p>
              )}
            </div>

            <Button className="h-12 w-full" onClick={salva} data-testid="save-reservation-btn">
              Registra la prenotazione
            </Button>
          </Card>

          {promemoria.rows.length > 0 && (
            <Card className="space-y-2 p-4" data-testid="promemoria-card">
              <p className="text-xs font-bold uppercase tracking-[0.16em] text-slate-500">
                Da confermare per domani ({promemoria.rows.length})
              </p>
              <p className="text-[11px] text-slate-500">
                Chi ha lasciato l'email riceve il promemoria da solo. Per gli altri tocca il pulsante:
                il messaggio è già scritto, basta inviarlo.
              </p>
              {promemoria.rows.map((r) => (
                <div key={r.id} className="border-t border-slate-100 pt-2 first:border-0" data-testid={`promemoria-${r.id}`}>
                  <p className="text-sm">
                    <span className="font-num font-bold">{r.time}</span> · {r.customer_name} · {r.people} pax
                    {r.table_name && ` · ${r.table_name}`}
                    {r.customer_no_shows > 0 && (
                      <span className="ml-2 rounded-full bg-rose-100 px-2 py-0.5 text-[10px] font-bold text-rose-900">
                        {r.customer_no_shows} assenze
                      </span>
                    )}
                  </p>
                  <div className="mt-1.5 flex flex-wrap items-center gap-2">
                  {r.confirmed_at ? (
                    <span className="rounded-full bg-emerald-100 px-2 py-1 text-[10px] font-bold text-emerald-900">confermata</span>
                  ) : (
                    <>
                      {r.reminded_at && (
                        <span className="rounded-full bg-slate-100 px-2 py-1 text-[10px] font-semibold text-slate-500">
                          {r.reminded_by === "email" ? "email inviata" : "messaggio inviato"}
                        </span>
                      )}
                      {r.whatsapp && (
                        <a href={r.whatsapp} target="_blank" rel="noreferrer" data-testid={`promemoria-wa-${r.id}`}
                           onClick={() => p(`/reservations/${r.id}/remind`, { channel: "whatsapp" }).then(load)}
                           className="rounded-full bg-emerald-600 px-3 py-1.5 text-[11px] font-bold text-white">
                          WhatsApp
                        </a>
                      )}
                      {r.sms && (
                        <a href={r.sms} data-testid={`promemoria-sms-${r.id}`}
                           onClick={() => p(`/reservations/${r.id}/remind`, { channel: "sms" }).then(load)}
                           className="rounded-full bg-slate-900 px-3 py-1.5 text-[11px] font-bold text-white">
                          SMS
                        </a>
                      )}
                      {!r.phone && !r.email && <span className="text-[11px] text-slate-400">nessun contatto</span>}
                      <button data-testid={`promemoria-ok-${r.id}`}
                              onClick={() => p(`/reservations/${r.id}/remind`, { channel: "voce", confirmed: true }).then(load)}
                              className="rounded-full bg-slate-100 px-3 py-1.5 text-[11px] font-semibold text-slate-600">
                        Ha confermato
                      </button>
                    </>
                  )}
                  </div>
                </div>
              ))}
            </Card>
          )}

          {direzione && (
            <Card className="space-y-2 p-4" data-testid="permessi-card">
              <p className="text-xs font-bold uppercase tracking-[0.16em] text-slate-500">Chi può prendere le prenotazioni</p>
              <p className="text-[11px] text-slate-500">
                Titolare e direttore di sala possono sempre. Qui decidi tu a chi altro dare il permesso.
              </p>
              {squadra.filter((s) => !["titolare", "direttore"].includes(s.role)).map((s) => (
                <div key={s.id} className="flex items-center gap-2 border-t border-slate-100 pt-2 first:border-0" data-testid={`permesso-${s.id}`}>
                  <span className="min-w-0 flex-1 truncate text-sm">
                    {s.name} <span className="text-slate-500">· {s.role_label}</span>
                  </span>
                  <Switch checked={!!s.can_book} data-testid={`permesso-switch-${s.id}`}
                          onCheckedChange={async (v) => {
                            await pa(`/staff/${s.id}`, { can_book: v });
                            setSquadra(await g("/staff"));
                            toast.success(v ? `${s.name} può prendere le prenotazioni` : `${s.name} non prende più le prenotazioni`);
                          }} />
                </div>
              ))}
            </Card>
          )}

          {titolare && (
            <Card className="space-y-2 p-4" data-testid="turni-card">
              <p className="text-xs font-bold uppercase tracking-[0.16em] text-slate-500">Turni della sera</p>
              <p className="text-[11px] text-slate-500">
                Scrivi gli orari separati da virgola: il personale li trova come pulsanti rapidi quando prenota.
              </p>
              <div className="flex flex-wrap items-center gap-2">
                <Input value={turni} onChange={(e) => setTurni(e.target.value)} className="h-10 w-40" data-testid="turni-input" />
              <Button size="sm" variant="outline" data-testid="turni-save-btn"
                      onClick={async () => {
                        const lista = turni.split(",").map((s) => s.trim()).filter((s) => /^\d{2}:\d{2}$/.test(s));
                        if (!lista.length) return toast.error("Scrivi gli orari così: 19:30, 21:15");
                        await pu("/settings", { ...settings, cover_charge: Number(settings.cover_charge) || 0, turns: lista });
                        await refresh();
                        toast.success("Turni aggiornati");
                      }}>
                Salva turni
              </Button>
              </div>
            </Card>
          )}

          {titolare && <CaparraConfig settings={settings} onSalva={refresh} />}

          <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3">
            {delGiorno.length === 0 && <p className="text-sm text-slate-500">Nessuna prenotazione per questa data.</p>}
            {orariTurno.map((h, i) => {
              const dentro = delGiorno.filter((r) => (i === 0 ? r.time < orariTurno[1] || !orariTurno[1] : r.time >= h));
              return dentro.length ? (
                <div key={h} className="space-y-3 md:col-span-2 xl:col-span-3" data-testid={`blocco-turno-${i + 1}`}>
                  <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-400">{i + 1}º turno · dalle {h}</p>
                  <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3">{dentro.map(scheda)}</div>
                </div>
              ) : null;
            })}
          </div>
        </>
      )}

      {vista === "settimana" && (
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-4 lg:grid-cols-7" data-testid="vista-settimana">
          {settimanaDi(data).map((d) => (
            <Card key={d} className={`p-3 ${d === oggi() ? "border-slate-900" : ""}`} data-testid={`sett-${d}`}>
              <button className="w-full text-left" onClick={() => { setData(d); setVista("giorno"); }}>
                <p className="text-[10px] font-bold uppercase tracking-wider text-slate-500">{giornoIt(d)}</p>
                <p className="font-num mt-1 text-lg font-bold">{coperti(d)}<span className="ml-1 text-[10px] font-semibold text-slate-400">coperti</span></p>
                <p className="text-[10px] text-slate-400">{perGiorno(d).length} prenotazioni</p>
              </button>
              <div className="mt-2 space-y-1">
                {perGiorno(d).sort((a, b) => a.time.localeCompare(b.time)).map((r) => (
                  <p key={r.id} className="truncate text-[11px] text-slate-600" data-testid={`sett-res-${r.id}`}>
                    <b className="font-num">{r.time}</b> {r.customer_name} {r.people}p {r.table_name}
                  </p>
                ))}
              </div>
            </Card>
          ))}
        </div>
      )}

      {vista === "attesa" && (
        <ListaAttesa data={data} zones={zones} onConvertita={load} />
      )}

      {vista === "mese" && (
        <div className="grid grid-cols-7 gap-1.5" data-testid="vista-mese">
          {["L", "M", "M", "G", "V", "S", "D"].map((g, i) => (
            <p key={`dow-${i}`} className="text-center text-[10px] font-bold uppercase text-slate-400">{g}</p>
          ))}
          {meseDi(data).map((d, i) => {
            if (!d) return <div key={`v${i}`} />;
            const c = coperti(d);
            const pieno = capienza ? c / capienza : 0;
            const oltre = capienza && c > capienza;
            return (
              <button key={d} data-testid={`mese-${d}`} onClick={() => { setData(d); setVista("giorno"); }}
                      className={`aspect-square rounded-md border p-1 text-left ${d === oggi() ? "border-slate-900" : "border-slate-200"} ${
                        oltre ? "bg-rose-100" : pieno > 0.66 ? "bg-emerald-300" : pieno > 0.33 ? "bg-emerald-200" : pieno > 0 ? "bg-emerald-100" : "bg-white"}`}>
                <span className="block text-[9px] text-slate-500">{Number(d.slice(8))}</span>
                {c > 0 && <span className="font-num block text-base font-bold leading-none">{c}</span>}
                {oltre && <span className="block text-[8px] font-bold text-rose-700">oltre</span>}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
